import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query'
import { MembershipCreditRulesApi } from '@trybeapp/sdk.customer'
import { MembershipCreditRulesApi as TsMembershipCreditRulesApi } from '@trybeapp/sdk'
import { LegacySdkResponseError, PaginatedApiResponse } from 'api/api.types'
import { apiConfiguration } from 'utilities/TrybeApiClient'
import {
  CreateMembershipCreditRule,
  ResponseError,
  ShowMembershipCreditRule,
  UpdateMembershipCreditRule,
} from '@trybeapp/sdk'

const api = new MembershipCreditRulesApi()
const tsApi = new TsMembershipCreditRulesApi(apiConfiguration)

export const useGetMembershipCreditRule = (
  creditRuleId: string,
  options: UseQueryOptions<ShowMembershipCreditRule, ResponseError> = {}
) => {
  return useQuery<ShowMembershipCreditRule, ResponseError>(
    ['membershipCreditRule', { creditRuleId }],
    () => tsApi.getMembershipCreditRule({ creditRuleId }),
    options
  )
}

export const useCreateMembershipCreditRule = () => {
  const queryClient = useQueryClient()

  return useMutation(
    (createMembershipCreditRule: CreateMembershipCreditRule) =>
      tsApi.createMembershipCreditRule({ createMembershipCreditRule }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('membershipCreditRules')
      },
    }
  )
}

export const useUpdateMembershipCreditRule = () => {
  const queryClient = useQueryClient()

  return useMutation(
    ({
      creditRuleId,
      updateMembershipCreditRule,
    }: {
      creditRuleId: string
      updateMembershipCreditRule: UpdateMembershipCreditRule
    }) => {
      return tsApi.updateMembershipCreditRule({ creditRuleId, updateMembershipCreditRule })
    },
    {
      onSuccess: (_, { creditRuleId }) => {
        queryClient.invalidateQueries(['membershipCreditRule', { creditRuleId }])
      },
    }
  )
}

export const useMembershipCreditRules = (
  params = {},
  options: UseQueryOptions = {}
): UseQueryResult<any> => {
  // With these defaults, data will always be refreshed
  // in the background if the cache is older than 1 hour.
  options.cacheTime = options.cacheTime ?? Infinity
  options.staleTime = options.staleTime ?? 60 * 60 * 1000 // 60 minutes

  return useQuery(
    ['membershipCreditRules', { params }],
    () => api.listMembershipCreditRules(params),
    options
  )
}

interface MembershipCreditRulesData {
  data: Array<Record<string, any>>
  page: number
  nextPage: number | undefined
  total: number
}

export const useInfiniteMembershipCreditRules = (params = {}) =>
  useInfiniteQuery<MembershipCreditRulesData, LegacySdkResponseError>(
    ['membershipCreditRules', { params }],
    fetchMembershipCreditRules,
    {
      getNextPageParam: (lastPage: any, pages) => {
        return lastPage.nextPage
      },
    }
  )

const fetchMembershipCreditRules = async ({ queryKey, pageParam: page = 1 }) => {
  const { params } = queryKey[1]

  const response: PaginatedApiResponse<any> = await api.listMembershipCreditRules({
    page,
    ...params,
  })

  return {
    data: response.data,
    page,
    nextPage: response.meta.last_page > page ? page + 1 : undefined,
    total: response.meta.total,
  }
}

export const useDeleteMembershipCreditRule = (creditRuleId?: string) => {
  const queryClient = useQueryClient()

  return useMutation<any, LegacySdkResponseError, string>(
    (creditRuleIdPassed) => api.deleteMembershipCreditRule(creditRuleId ?? creditRuleIdPassed),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('membershipCreditRules')
      },
    }
  )
}

interface IssueCreditRuleParams {
  creditRuleId: string
  includeUpcoming?: boolean
}

export const useIssueCreditRule = () => {
  const queryClient = useQueryClient()

  return useMutation<void, ResponseError, IssueCreditRuleParams>(
    ({ creditRuleId, includeUpcoming = false }) =>
      api.issueMembershipCreditRuleCredits(creditRuleId, {
        issueMembershipCreditRuleCreditsRequest: { include_upcoming: includeUpcoming },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['customerCredits'])
      },
    }
  )
}
